/* eslint-disable complexity */
import React, { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Text, Divider, Link, Grid
} from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import UrlUtils from 'utils/Url';
import numberUtils from 'utils/Number';
import jsUtils from 'utils/javascript';
import UI from 'utils/UI';
import Storage from 'utils/localStorage/Storage';
import bazaarVoiceApi from 'services/api/thirdparty/BazaarVoice';
import ReviewsImageCarousel from 'components/ProductPage/ReviewsImageCarousel/ReviewsImageCarousel';
import Chiclet from 'components/Chiclet';
import Review from 'components/ProductPage/RatingsAndReviews/Review/Review';
import ReviewsStats from 'components/ProductPage/RatingsAndReviews/ReviewsStats/ReviewsStats';
import ReviewsFilters from 'components/ProductPage/RatingsAndReviews/ReviewsFilters/ReviewsFilters';
import Pagination from 'components/ProductPage/Pagination/Pagination';
import ProductActions from 'actions/ProductActions';
import Actions from 'actions/Actions';
import SectionDivider from 'components/SectionDivider/SectionDivider';
import { productSelector } from 'selectors/page/product/productSelector';
import { localeSelector } from 'viewModel/selectors/locale/localeSelector';
import { isGenAIPRSEnabledSelector } from 'ai/selectors/killSwitches/isGenAIPRSEnabledSelector';
import { trackWriteReviewClick } from 'ai/analytics/trackers/prsTrackers';
import { useReviewsData } from 'components/ProductPage/RatingsAndReviews/hooks/useReviewsData';
import RatingsAndReviewsEN from 'components/ProductPage/RatingsAndReviews/locales/RatingsAndReviews_en_US';
import RatingsAndReviewsFR from 'components/ProductPage/RatingsAndReviews/locales/RatingsAndReviews_fr_CA';

const { formatReviewCount } = numberUtils;
const PRODUCT_ADD_REVIEWS_URL = '/addReview?productId=';
const PAGE_SIZE = 6;
const getText = localeUtils.getLocalisedDataFromResource(RatingsAndReviewsEN, RatingsAndReviewsFR);

const RatingsAndReviews = props => {
    const {
        productId,
        currentProduct,
        isSmallView,
        reviewsToShow,
        showAddDropDownReviewSection,
        reviewStatistics: incomingReviewStatistics,
        initialTotalReviews: incomingInitialTotalReviews
    } = props;

    const dispatch = useDispatch();
    const selectedSentiment = useSelector(state => productSelector(state)?.selectedSentiment);
    const language = useSelector(localeSelector);
    const showPrsModule = useSelector(isGenAIPRSEnabledSelector);

    const {
        state,
        loadInitialReviews,
        initReviews,
        filterReviews,
        handlePageClick,
        handleReviewImageOnclick: rawHandleReviewImageOnclick,
        getRemappedReviewFilters
    } = useReviewsData({
        productId,
        currentProduct,
        selectedSentiment,
        language,
        showPrsModule,
        incomingReviewStatistics,
        incomingInitialTotalReviews,
        showAddDropDownReviewSection,
        loadHighlightedReviewsAction: ProductActions.loadHighlightedReviews,
        reviewsToShow
    });

    // Bump this counter on every image click so the openImageModal effect re-fires
    // even when the user clicks the same review image twice.
    const [imageClickToken, setImageClickToken] = useState(0);
    const handleReviewImageOnclick = useCallback(
        (selectedReviewId, reviewSelectedPhotoId) => {
            rawHandleReviewImageOnclick(selectedReviewId, reviewSelectedPhotoId);
            setImageClickToken(token => token + 1);
        },
        [rawHandleReviewImageOnclick]
    );

    useEffect(() => {
        initReviews({ incomingReviewStatistics, incomingInitialTotalReviews, showAddDropDownReviewSection, reset: false });
    }, []);

    const redirectToAddReviewPage = useCallback(() => {
        if (showPrsModule) {
            trackWriteReviewClick({ productId, uiVariant: isSmallView ? 'SMUI' : 'LGUI' });
        }

        if (state.reviewStatistics) {
            const storageKey = bazaarVoiceApi.getStorageKeyForProductReview(productId);
            try {
                Storage.local.setItem(storageKey, state.reviewStatistics);
            } catch (_) {
                /* noop */
            }
        }

        UrlUtils.redirectTo(PRODUCT_ADD_REVIEWS_URL + productId);
    }, [productId, showPrsModule, isSmallView, state.reviewStatistics]);

    const clearSentiment = useCallback(() => {
        dispatch(ProductActions.setSelectedSentiment(''));
        dispatch(ProductActions.removeHighlightedReviews());
    }, [dispatch]);

    const openImageModal = useCallback(() => {
        const { user, reviewsWithImage, reviewSelectedPhotoId, selectedReviewId } = state;
        const reviewSelected = jsUtils.findInArray(reviewsWithImage, review => review.reviewId === selectedReviewId);
        dispatch(
            Actions.showReviewImageModal({
                isOpen: true,
                reviewSelected,
                reviewSelectedIndex: reviewsWithImage.findIndex(review => review.reviewId === selectedReviewId),
                reviewsWithImage,
                reviewsReference: reviewsWithImage,
                reviewUser: user,
                reviewProductTitle: currentProduct.productDetails.displayName,
                reviewSelectedPhotoId,
                isFromImageCarousel: false
            })
        );
    }, [state, currentProduct, dispatch]);

    useEffect(() => {
        if (imageClickToken > 0 && state.selectedReviewId !== null && state.reviewsWithImage.length > 0) {
            openImageModal();
        }
    }, [imageClickToken, state.selectedReviewId, state.reviewSelectedPhotoId, state.reviewsWithImage]);

    const { isReviewsFiltersMasterListEnabled } = Sephora.configurationSettings;
    let { reviews } = state;
    const {
        currentPage,
        totalReviews,
        hasReviews,
        isSearchPerformed,
        searchKeyword,
        reviewStatistics,
        helpfulReviews,
        initialTotalReviews,
        showProsAndCons,
        categoryId,
        sentimentLoading
    } = state;
    const { user } = state;
    const totalReviewCount = totalReviews || 0;

    if (helpfulReviews && helpfulReviews.length && currentPage === 1) {
        reviews = helpfulReviews.concat(reviews.filter(review => !helpfulReviews.some(helpful => helpful.reviewId === review.reviewId)));
    }

    const totalReviewPages = Math.ceil(totalReviews / PAGE_SIZE);
    const maxReviewPages = 999;
    const totalReviewsBreadcrumb = totalReviewCount > 999 ? `${Math.floor(totalReviewCount / 1000)}k` : totalReviewCount;
    const breadcrumbFromPosition = (currentPage - 1) * PAGE_SIZE + 1;
    const breadcrumbToPosition = PAGE_SIZE * currentPage > totalReviewCount ? totalReviewCount : PAGE_SIZE * currentPage;
    const totalReviewsCount = selectedSentiment ? totalReviews : initialTotalReviews;
    const reviewFilters = isReviewsFiltersMasterListEnabled?.isEnabled ? getRemappedReviewFilters() : currentProduct.reviewFilters;

    return (
        <React.Fragment>
            {totalReviewCount ? <SectionDivider marginY={null} /> : <Divider />}
            <Box paddingBottom={totalReviewCount ? [6, 8] : [4, 5]}>
                <Grid
                    gap={1}
                    lineHeight='tight'
                    columns={['1fr auto', null, 1]}
                    alignItems='baseline'
                >
                    <Text
                        is='h2'
                        marginTop='1em'
                        fontSize={['md', 'lg']}
                        fontWeight='bold'
                        data-at='ratings_reviews_section'
                        children={`${getText('ratingsReviews')} (${formatReviewCount(totalReviewsCount || 0)})`}
                    />
                    <div>
                        <Link
                            padding={2}
                            margin={-2}
                            color='blue'
                            onClick={redirectToAddReviewPage}
                            children={getText('writeReview')}
                        />
                    </div>
                </Grid>
                <ReviewsStats
                    sentiments={currentProduct.sentiments}
                    reviewStatistics={reviewStatistics}
                    totalReviewCount={initialTotalReviews}
                    showProsAndCons={showProsAndCons}
                    productId={productId}
                />
                {currentProduct.reviewImages && !selectedSentiment && (
                    <ReviewsImageCarousel
                        carouselContextId={productId}
                        productId={productId}
                        productTitle={currentProduct.productDetails.displayName}
                        reviewImages={currentProduct.reviewImages}
                        user={user}
                    />
                )}
                {(reviewStatistics || isSearchPerformed) && !selectedSentiment ? (
                    <ReviewsFilters
                        filtersConfiguration={reviewFilters}
                        currentProduct={currentProduct}
                        productId={productId}
                        categoryId={categoryId}
                        isSearchPerformed={isSearchPerformed}
                        loadInitialReviews={loadInitialReviews}
                    />
                ) : null}
                {selectedSentiment && showPrsModule && (
                    <Box marginTop={[5, 6]}>
                        <Chiclet
                            variant='fill'
                            showX={true}
                            onClick={clearSentiment}
                        >
                            {selectedSentiment}
                        </Chiclet>
                        {!sentimentLoading && hasReviews && (
                            <Text
                                is='p'
                                fontSize='sm'
                                lineHeight='tight'
                                color='gray'
                                marginTop={4}
                            >
                                {getText('reviewsFromToMentioning', [breadcrumbFromPosition, breadcrumbToPosition, totalReviewsBreadcrumb])}
                                {' "'}
                                <Text
                                    is='span'
                                    fontWeight='bold'
                                >
                                    {selectedSentiment}
                                </Text>
                                {'"'}
                            </Text>
                        )}
                    </Box>
                )}
                {isSearchPerformed && !selectedSentiment && (
                    <Text
                        is='h3'
                        lineHeight='tight'
                        fontWeight='bold'
                        data-at={hasReviews ? null : Sephora.debug.dataAt('rr_no_results_message')}
                        marginBottom={hasReviews ? 3 : 2}
                        children={
                            hasReviews
                                ? getText(totalReviews === 1 ? 'searchResult' : 'searchResults', [totalReviews, searchKeyword])
                                : getText('noSearchResult', [searchKeyword])
                        }
                    />
                )}
                {sentimentLoading && (
                    <Box marginTop={[5, 6]}>
                        {[1, 2, 3].map(i => (
                            <Box
                                key={i}
                                marginBottom={5}
                            >
                                <Box
                                    height={12}
                                    width='30%'
                                    marginBottom={2}
                                    baseCss={UI.SKELETON_ANIMATION}
                                />
                                <Box
                                    height={12}
                                    width='100%'
                                    marginBottom={1}
                                    baseCss={UI.SKELETON_ANIMATION}
                                />
                                <Box
                                    height={12}
                                    width='80%'
                                    baseCss={UI.SKELETON_ANIMATION}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
                {!sentimentLoading && hasReviews ? (
                    <Box marginTop={[5, 6]}>
                        {!(selectedSentiment && showPrsModule) && (
                            <Text
                                is='p'
                                fontSize='sm'
                                lineHeight='tight'
                                color='gray'
                                marginBottom={3}
                                children={getText('reviewsFromTo', [breadcrumbFromPosition, breadcrumbToPosition, totalReviewsBreadcrumb])}
                            />
                        )}
                        {reviews.map((item, index) => (
                            <React.Fragment key={item.reviewId}>
                                {!item.isHighlighted && index === 0 && isSmallView && <Divider marginBottom={3} />}
                                <Review
                                    index={index}
                                    {...item}
                                    refreshMethod={filterReviews}
                                    isSmallView={isSmallView}
                                    currentProduct={currentProduct}
                                    reviewImageModalTrigger={handleReviewImageOnclick}
                                    user={user}
                                />
                                {!item.isHighlighted && index < reviews.length - 1 && (
                                    <Divider
                                        marginTop={[4, 6]}
                                        marginBottom={5}
                                        {...(isSmallView && { thick: true, marginX: '-container' })}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </Box>
                ) : !sentimentLoading ? (
                    <React.Fragment>
                        {!isSearchPerformed && reviewStatistics !== undefined && (
                            <Text
                                is='p'
                                fontWeight='bold'
                                marginTop={4}
                                marginBottom={3}
                                children={getText('noReview')}
                            />
                        )}
                    </React.Fragment>
                ) : null}
                {!sentimentLoading && totalReviewPages > 1 && (
                    <Pagination
                        marginTop={[6, 7]}
                        totalPages={totalReviewPages > maxReviewPages ? maxReviewPages : totalReviewPages}
                        currentPage={currentPage}
                        scrollElementId='custom_filters'
                        handlePageClick={handlePageClick}
                    />
                )}
            </Box>
        </React.Fragment>
    );
};

export default wrapFunctionalComponent(RatingsAndReviews, 'RatingsAndReviews');
